import { useState } from "react";
import type { ChatSession } from "../types/index.js";
import { isDigestSession } from "../lib/digest.js";

interface ChatSessionListProps {
	sessions: ChatSession[];
	activeSessionId: string | null;
	loading: boolean;
	onSelect: (id: string | null) => void;
	onDelete: (id: string) => Promise<void>;
	/** Called when the user wants to start a new chat (title-input step). */
	onNewChat: () => void;
	/** When true, the "New Chat" button is disabled to prevent concurrent actions. */
	isStreaming?: boolean;
	/** Zoho user ID of the currently authenticated user — used to split My/Team sessions. */
	currentUserId?: string;
}

/**
 * Formats a UTC date string as a human-readable relative time label.
 * Examples: "Just now", "2h ago", "Yesterday", "3d ago", "14 Feb"
 */
function formatRelativeTime(dateString: string): string {
	const date = new Date(dateString);
	if (isNaN(date.getTime())) return "Unknown";
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	if (diffMs < 0) return "Just now";
	const diffMins = Math.floor(diffMs / 60_000);
	const diffHours = Math.floor(diffMs / 3_600_000);
	const diffDays = Math.floor(diffMs / 86_400_000);

	if (diffMins < 2) return "Just now";
	if (diffMins < 60) return `${diffMins}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays === 1) return "Yesterday";
	if (diffDays < 7) return `${diffDays}d ago`;

	// Older than a week: show short date
	return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/**
 * Extracts initials from a display name (e.g. "Bob Patel" → "BP").
 */
function getInitials(name: string): string {
	return name
		.split(/\s+/)
		.filter(Boolean)
		.map((part) => part[0].toUpperCase())
		.slice(0, 2)
		.join("");
}

/**
 * Sidebar chat session list.
 *
 * When currentUserId is provided, splits sessions into "My Chats" (owned)
 * and "Team Chats" (created by other members). Owner-only controls (delete,
 * rename) are shown only on owned sessions. Team chat items show the
 * creator's display name and initials.
 */
export default function ChatSessionList({
	sessions,
	activeSessionId,
	loading,
	onSelect,
	onDelete,
	onNewChat,
	isStreaming = false,
	currentUserId,
}: ChatSessionListProps) {
	const [renamingId, setRenamingId] = useState<string | null>(null);
	const [showAllDigests, setShowAllDigests] = useState(false);

	async function handleDelete(event: React.MouseEvent, sessionId: string): Promise<void> {
		event.stopPropagation();
		try {
			await onDelete(sessionId);
		} catch {
			// Deletion failures are surfaced by the parent hook; silently ignore here
		}
	}

	// Split sessions into three groups: digests, owned, and team.
	// Digest sessions are always separated regardless of currentUserId.
	const digestSessions = sessions.filter(isDigestSession);
	const ownedSessions = currentUserId
		? sessions.filter((s) => !isDigestSession(s) && s.ownerId === currentUserId)
		: sessions.filter((s) => !isDigestSession(s));
	const teamSessions = currentUserId
		? sessions.filter((s) => !isDigestSession(s) && s.ownerId !== currentUserId)
		: [];

	const hasDigests = digestSessions.length > 0;
	const hasOwned = ownedSessions.length > 0;
	const hasTeam = teamSessions.length > 0;

	/** Renders a digest session row. Digest sessions are read-only — no rename or delete controls. */
	function renderDigestItem(session: ChatSession) {
		const isActive = activeSessionId === session.id;
		const title = session.title ?? "Weekly Digest";

		return (
			<div key={session.id} className="session-item-row">
				<button
					type="button"
					className={`session-item session-item--digest${isActive ? " session-item--active" : ""}`}
					onClick={() => onSelect(session.id)}
					aria-current={isActive ? true : undefined}
					aria-label={title}
					title={title}
				>
					{/* Bar-chart icon distinguishing digest sessions from regular chats */}
					<span
						role="img"
						aria-label="Weekly Digest"
						className="session-item-digest-icon"
					>
						<svg
							width="14"
							height="14"
							viewBox="0 0 16 16"
							fill="none"
							aria-hidden="true"
						>
							<rect x="1" y="10" width="2" height="5" rx="0.5" fill="currentColor" />
							<rect x="5" y="7" width="2" height="8" rx="0.5" fill="currentColor" />
							<rect x="9" y="4" width="2" height="11" rx="0.5" fill="currentColor" />
							<rect x="13" y="1" width="2" height="14" rx="0.5" fill="currentColor" />
						</svg>
					</span>
					<span className="session-item-content">
						<span className="session-item-title">{title}</span>
						<span className="session-item-time">
							{formatRelativeTime(session.updatedAt)}
						</span>
					</span>
				</button>
				{/* No rename or delete controls for digest sessions */}
			</div>
		);
	}

	/** Renders a single session row. isOwned controls whether edit/delete controls appear. */
	function renderSessionItem(session: ChatSession, isOwned: boolean) {
		const isActive = activeSessionId === session.id;
		const title = session.title ?? "Untitled chat";

		return (
			<div key={session.id} className="session-item-row">
				<button
					type="button"
					className={`session-item${isActive ? " session-item--active" : ""}`}
					onClick={() => onSelect(session.id)}
					aria-current={isActive ? true : undefined}
					aria-label={title}
					title={title}
				>
					{/* Team session: show creator initials avatar */}
					{!isOwned && session.creatorDisplayName && (
						<span
							className="session-item-creator-avatar"
							aria-hidden="true"
							title={session.creatorDisplayName}
						>
							{getInitials(session.creatorDisplayName)}
						</span>
					)}
					<span className="session-item-content">
						<span className="session-item-title">{title}</span>
						{!isOwned && session.creatorDisplayName && (
							<span className="session-item-creator-name">
								{session.creatorDisplayName}
							</span>
						)}
						<span className="session-item-time">
							{formatRelativeTime(session.updatedAt)}
						</span>
					</span>
				</button>
				{/* Owner controls: rename and delete */}
				{isOwned && (
					<>
						<button
							type="button"
							className="session-rename-btn"
							onClick={(e) => {
								e.stopPropagation();
								setRenamingId(session.id);
							}}
							aria-label={`Rename chat: ${title}`}
							title="Rename this chat"
						>
							<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
								<path d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>
						<button
							type="button"
							className="session-delete-btn"
							onClick={(e) => handleDelete(e, session.id)}
							aria-label={`Delete chat: ${title}`}
							title="Delete this chat"
						>
							&times;
						</button>
					</>
				)}
			</div>
		);
	}

	return (
		<div className="session-list-container">
			{/* New chat button — fixed at top, does not scroll away */}
			<button
				type="button"
				className="session-new-btn"
				onClick={onNewChat}
				disabled={isStreaming}
				aria-label="Start a new chat"
			>
				<span aria-hidden="true">+</span>
				New Chat
			</button>

			{/* Session list body — independently scrollable */}
			<div className="session-list-scroll">
				{loading ? (
					<div className="session-list-loading">
						<div className="loading-spinner" />
						<span>Loading chats...</span>
					</div>
				) : sessions.length === 0 ? (
					<p className="session-list-empty">No previous chats</p>
				) : (
					<div className="session-list">
						{/* Digests section — rendered first, above My Chats and Team Chats */}
						{hasDigests && (
							<section role="region" aria-label="Digests">
								<h3 className="session-section-heading">Digests</h3>
								{(showAllDigests ? digestSessions : digestSessions.slice(0, 4)).map((session) =>
									renderDigestItem(session),
								)}
								{digestSessions.length > 4 && (
									<button
										type="button"
										className="digest-show-more-btn"
										onClick={() => setShowAllDigests((prev) => !prev)}
									>
										{showAllDigests ? "Show fewer digests" : "Show older digests"}
									</button>
								)}
							</section>
						)}

						{/* My Chats section */}
						{hasOwned && (
							<section role="region" aria-label="My Chats">
								<h3 className="session-section-heading">My Chats</h3>
								{ownedSessions.map((session) =>
									renderSessionItem(session, true),
								)}
							</section>
						)}

						{/* Team Chats section */}
						{hasTeam && (
							<section role="region" aria-label="Team Chats">
								<h3 className="session-section-heading">Team Chats</h3>
								{teamSessions.map((session) =>
									renderSessionItem(session, false),
								)}
							</section>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
